import React, { useState, useEffect } from 'react';
import { apiFetch, getUserFacingErrorMessage } from '../services/apiClient';
import './MonitoringDashboard.css';

/** Hide Monitoring Overview tab for all roles (UI temporarily disabled). */
const SHOW_OVERVIEW_TAB = false;
/** Hide Reliability Metrics / Trends cards on Model Performance (keep Drift Detection). */
const SHOW_RELIABILITY_METRICS = false;
const SHOW_RELIABILITY_TRENDS = false;

const MonitoringDashboard = () => {
  const [activeTab, setActiveTab] = useState(
    SHOW_OVERVIEW_TAB ? 'overview' : 'model-performance'
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [infoModal, setInfoModal] = useState(null); // Track which info modal is open
  const [personaView, setPersonaView] = useState('unified'); // 'unified', 'exec', 'recruiter', 'compliance'
  
  
  // Overview data
  const [overviewData, setOverviewData] = useState(null);
  
  // Tracing data
  const [traces, setTraces] = useState([]);
  const [selectedTrace, setSelectedTrace] = useState(null);
  const [traceDetails, setTraceDetails] = useState(null);
  const [traceFilters, setTraceFilters] = useState({
    company_id: '',
    start_date: '',
    end_date: '',
    operation: '',
    limit: 50
  });
  
  
  
  // Model performance data
  const [modelPerformance, setModelPerformance] = useState(null);
  const [modelDrift, setModelDrift] = useState(null);
  const [modelTrends, setModelTrends] = useState([]);
  const [modelFilters, setModelFilters] = useState({
    model_name: 'evaluator',
    company_id: '',
    days: 30
  });
  
  // RAG metrics data
  const [ragMetrics, setRagMetrics] = useState(null);
  const [ragFilters, setRagFilters] = useState({
    company_id: '',
    job_id: ''
  });

  

  // Companies Token Tracker (hierarchy: company -> job -> resume)
  const [companyHierarchy, setCompanyHierarchy] = useState(null);
  const [companyHierarchyError, setCompanyHierarchyError] = useState(null);
  const [companyTrackerRange, setCompanyTrackerRange] = useState('30');
  const [companyTrackerExpandedJobs, setCompanyTrackerExpandedJobs] = useState(new Set());
  const [companyTrackerSearch, setCompanyTrackerSearch] = useState('');
  const [companyTrackerPage, setCompanyTrackerPage] = useState(1);
  const [companyTrackerPageSize, setCompanyTrackerPageSize] = useState(20);
  const [companyTrackerMeta, setCompanyTrackerMeta] = useState({ total_jobs: 0, page: 1, page_size: 20, total_pages: 0 });
  const [companyTrackerResetting, setCompanyTrackerResetting] = useState(false);
  const [companyTrackerViewMode, setCompanyTrackerViewMode] = useState('official');
  const [costSyncing, setCostSyncing] = useState(false);
  const [authCompanyId, setAuthCompanyId] = useState('');

  const showOverviewTab = SHOW_OVERVIEW_TAB;

  useEffect(() => {
    fetchAuthCompanyId();
  }, []);

  useEffect(() => {
    if (!showOverviewTab) {
      if (activeTab === 'overview') {
        setActiveTab('model-performance');
      }
      setLoading(false);
      return;
    }
    fetchOverviewData();
  }, [showOverviewTab]);

  const fetchAuthCompanyId = async () => {
    try {
      const response = await apiFetch('/api/auth/status');
      if (!response.ok) return;
      const data = await response.json();
      const cid = data?.user?.company_id != null ? String(data.user.company_id) : '';
      if (!cid) return;

      setAuthCompanyId(cid);
      setModelFilters(prev => ({ ...prev, company_id: prev.company_id || cid }));
      setRagFilters(prev => ({ ...prev, company_id: prev.company_id || cid }));
      setTraceFilters(prev => ({ ...prev, company_id: prev.company_id || cid }));
    } catch (err) {
      console.error('Error fetching auth company scope:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'model-performance') {
      if (SHOW_RELIABILITY_METRICS) fetchModelPerformance();
      fetchModelDrift();
      if (SHOW_RELIABILITY_TRENDS) fetchModelTrends();
    } else if (activeTab === 'company-tracker') {
      fetchCompanyTokenHierarchy();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'company-tracker') {
      fetchCompanyTokenHierarchy();
    }
  }, [activeTab, companyTrackerRange, companyTrackerSearch, companyTrackerPage, companyTrackerPageSize, companyTrackerViewMode, authCompanyId]);

  useEffect(() => {
    const savedMode = window.localStorage.getItem('monitoring_company_tracker_view_mode');
    if (savedMode === 'official' || savedMode === 'personal') {
      setCompanyTrackerViewMode(savedMode);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem('monitoring_company_tracker_view_mode', companyTrackerViewMode);
  }, [companyTrackerViewMode]);

  useEffect(() => {
    if (activeTab === 'model-performance') {
      fetchModelPerformance();
      fetchModelDrift();
      fetchModelTrends();
    }
  }, [modelFilters]);

  useEffect(() => {
    if (selectedTrace) {
      fetchTraceDetails(selectedTrace);
    }
  }, [selectedTrace]);

  const fetchOverviewData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:98',message:'Starting fetchOverviewData',data:{timestamp:Date.now()},timestamp:Date.now(),sessionId:'debug-session'})}).catch(()=>{});
      // #endregion
      
      // Fetch health status
      const healthResponse = await apiFetch('/api/monitoring/health');
      const healthData = healthResponse.ok ? await healthResponse.json() : null;
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:105',message:'Health response received',data:{ok:healthResponse.ok,status:healthResponse.status,hasData:!!healthData,dataKeys:healthData?Object.keys(healthData):[]},timestamp:Date.now(),sessionId:'debug-session'})}).catch(()=>{});
      // #endregion
      
      // Fetch recent alerts
      const alertsResponse = await apiFetch('/api/monitoring/alerts?hours=24');
      const alertsData = alertsResponse.ok ? await alertsResponse.json() : null;
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:109',message:'Alerts response received',data:{ok:alertsResponse.ok,status:alertsResponse.status,hasData:!!alertsData},timestamp:Date.now(),sessionId:'debug-session'})}).catch(()=>{});
      // #endregion
      
      // Fetch RAG metrics summary
      const ragResponse = await apiFetch('/api/monitoring/rag-metrics/summary');
      const ragData = ragResponse.ok ? await ragResponse.json() : null;
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:113',message:'RAG metrics response received',data:{ok:ragResponse.ok,status:ragResponse.status,hasData:!!ragData,dataStructure:ragData?JSON.stringify(ragData).substring(0,200):'null'},timestamp:Date.now(),sessionId:'debug-session'})}).catch(()=>{});
      // #endregion
      
      // Fetch cost summary (last 30 days)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      const costResponse = await apiFetch(`/api/monitoring/costs?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`);
      const costData = costResponse.ok ? await costResponse.json() : null;
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:120',message:'Cost response received',data:{ok:costResponse.ok,status:costResponse.status,hasData:!!costData},timestamp:Date.now(),sessionId:'debug-session'})}).catch(()=>{});
      // #endregion
      
      // Fetch quality summary (last 30 days) - internal metrics
      const qualityResponse = await apiFetch('/api/monitoring/quality/summary?days=30');
      const qualityData = qualityResponse.ok ? await qualityResponse.json() : null;
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:quality_fetch',message:'Quality response received',data:{ok:qualityResponse.ok,status:qualityResponse.status,hasData:!!qualityData},timestamp:Date.now(),sessionId:'debug-session'})}).catch(()=>{});
      // #endregion
      
      // Fetch quality confidence (customer-facing metrics)
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:confidence_fetch:before',message:'About to fetch confidence endpoint',data:{url:'/api/monitoring/quality/confidence?days=30'},timestamp:Date.now(),sessionId:'debug-session',runId:'confidence-debug',hypothesisId:'C4'})}).catch(()=>{});
      // #endregion
      const confidenceResponse = await apiFetch('/api/monitoring/quality/confidence?days=30');
      const confidenceData = confidenceResponse.ok ? await confidenceResponse.json() : null;
      
      // Fetch cost efficiency metrics (production-ready cost tracking)
      const costEfficiencyResponse = await apiFetch('/api/monitoring/cost-efficiency?days=30');
      const costEfficiencyData = costEfficiencyResponse.ok ? await costEfficiencyResponse.json() : null;
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:confidence_fetch:after',message:'Confidence response received',data:{ok:confidenceResponse.ok,status:confidenceResponse.status,hasData:!!confidenceData,hasSuccess:!!confidenceData?.success,hasDataField:!!confidenceData?.data,dataKeys:confidenceData?.data?Object.keys(confidenceData.data):[],decisionConfidence:confidenceData?.data?.decision_confidence,evidenceIntegrity:confidenceData?.data?.evidence_integrity,totalJudgments:confidenceData?.data?.summary?.total_judgments},timestamp:Date.now(),sessionId:'debug-session',runId:'confidence-debug',hypothesisId:'C5'})}).catch(()=>{});
      // #endregion
      
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:144',message:'Before constructing overviewDataObj',data:{healthDataStructure:healthData?JSON.stringify(healthData).substring(0,300):'null',alertsDataStructure:alertsData?JSON.stringify(alertsData).substring(0,300):'null',ragDataStructure:ragData?JSON.stringify(ragData).substring(0,300):'null',costDataStructure:costData?JSON.stringify(costData).substring(0,300):'null',qualityDataStructure:qualityData?JSON.stringify(qualityData).substring(0,300):'null',confidenceDataStructure:confidenceData?JSON.stringify(confidenceData).substring(0,300):'null'},timestamp:Date.now(),sessionId:'debug-session',runId:'overview-debug',hypothesisId:'H1'})}).catch(()=>{});
      // #endregion
      
      const overviewDataObj = {
        health: healthData?.data || null,
        alerts: alertsData?.data || null,
        rag: ragData?.data || null,
        cost: costData?.data || null,
        quality: qualityData?.data || null,  // Internal metrics (for technical details)
        confidence: confidenceData?.data || null,  // Customer-facing metrics
        costEfficiency: costEfficiencyData?.data || null  // Production-ready cost efficiency
      };
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:151',message:'After constructing overviewDataObj',data:{hasHealth:!!overviewDataObj.health,hasAlerts:!!overviewDataObj.alerts,hasRag:!!overviewDataObj.rag,hasCost:!!overviewDataObj.cost,hasQuality:!!overviewDataObj.quality,hasConfidence:!!overviewDataObj.confidence,healthKeys:overviewDataObj.health?Object.keys(overviewDataObj.health):[],alertsKeys:overviewDataObj.alerts?Object.keys(overviewDataObj.alerts):[],ragKeys:overviewDataObj.rag?Object.keys(overviewDataObj.rag):[],costKeys:overviewDataObj.cost?Object.keys(overviewDataObj.cost):[],qualityKeys:overviewDataObj.quality?Object.keys(overviewDataObj.quality):[],confidenceKeys:overviewDataObj.confidence?Object.keys(overviewDataObj.confidence):[]},timestamp:Date.now(),sessionId:'debug-session',runId:'overview-debug',hypothesisId:'H2'})}).catch(()=>{});
      // #endregion
      
      setOverviewData(overviewDataObj);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching overview data:', err);
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:131',message:'Error in fetchOverviewData',data:{error:err.message,stack:err.stack?.substring(0,200)},timestamp:Date.now(),sessionId:'debug-session'})}).catch(()=>{});
      // #endregion
      setError('Failed to load overview data');
      setLoading(false);
    }
  };

  const fetchTraces = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (traceFilters.company_id) params.append('company_id', traceFilters.company_id);
      if (traceFilters.start_date) params.append('start_date', traceFilters.start_date);
      if (traceFilters.end_date) params.append('end_date', traceFilters.end_date);
      if (traceFilters.operation) params.append('operation', traceFilters.operation);
      params.append('limit', traceFilters.limit);
      
      const response = await apiFetch(`/api/monitoring/traces?${params}`);
      if (response.ok) {
        const data = await response.json();
        setTraces(data.data?.traces || []);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching traces:', err);
      setError('Failed to load traces');
      setLoading(false);
    }
  };

  const fetchTraceDetails = async (traceId) => {
    try {
      const response = await apiFetch(`/api/monitoring/traces/${traceId}`);
      if (response.ok) {
        const data = await response.json();
        setTraceDetails(data.data);
      }
    } catch (err) {
      console.error('Error fetching trace details:', err);
    }
  };

  const fetchCompanyTokenHierarchy = async () => {
    const cid = authCompanyId;
    setCompanyHierarchyError(null);
    if (!cid) {
      setCompanyHierarchyError('No company scope available for this user.');
      setCompanyHierarchy(null);
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append('company_id', String(cid));
      params.append('include_costs', 'true');
      params.append('since_checkpoint', companyTrackerViewMode === 'personal' ? 'true' : 'false');
      if (companyTrackerViewMode === 'official') {
        if (companyTrackerRange === 'all') {
          params.append('start_date', '1970-01-01T00:00:00Z');
        } else {
          params.append('days', String(Number(companyTrackerRange)));
        }
      } else {
        // In personal mode, backend locks to checkpoint when available.
        params.append('days', String(Number(companyTrackerRange) || 30));
      }
      if (companyTrackerSearch.trim()) params.append('q', companyTrackerSearch.trim());
      params.append('page', String(companyTrackerPage));
      params.append('page_size', String(companyTrackerPageSize));
      const response = await apiFetch(`/api/monitoring/usage/company-hierarchy?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setCompanyHierarchy(data.data || null);
        const meta = data?.data?.pagination || { total_jobs: 0, page: 1, page_size: companyTrackerPageSize, total_pages: 0 };
        setCompanyTrackerMeta(meta);
        if (meta.page && meta.page !== companyTrackerPage) {
          setCompanyTrackerPage(meta.page);
        }
      } else {
        setCompanyHierarchyError(`API ${response.status}: ${response.statusText}`);
        setCompanyHierarchy(null);
        setCompanyTrackerMeta({ total_jobs: 0, page: 1, page_size: companyTrackerPageSize, total_pages: 0 });
      }
    } catch (err) {
      setCompanyHierarchyError(err.message || 'Failed to load company token usage');
      setCompanyHierarchy(null);
      setCompanyTrackerMeta({ total_jobs: 0, page: 1, page_size: companyTrackerPageSize, total_pages: 0 });
    }
  };

  const resetCompanyTokenHierarchy = async () => {
    const cid = authCompanyId;
    if (!cid) {
      setCompanyHierarchyError('No company scope available for this user.');
      return;
    }
    const confirmReset = window.confirm(
      'This will reset the Company Token Tracker view to start from now for your account. Historical logs are preserved. Continue?'
    );
    if (!confirmReset) return;

    setCompanyTrackerResetting(true);
    setCompanyHierarchyError(null);
    try {
      const response = await apiFetch(
        `/api/monitoring/usage/company-hierarchy/reset?company_id=${encodeURIComponent(cid)}`,
        { method: 'POST' }
      );
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(
          getUserFacingErrorMessage(
            response.status,
            errData,
            `HTTP ${response.status}`
          )
        );
      }
      setCompanyTrackerExpandedJobs(new Set());
      setCompanyTrackerPage(1);
      await fetchCompanyTokenHierarchy();
    } catch (err) {
      setCompanyHierarchyError(err.message || 'Failed to reset company token usage');
    } finally {
      setCompanyTrackerResetting(false);
    }
  };

  const formatSyncAge = (isoString) => {
    if (!isoString) return null;
    const diffMs = Date.now() - new Date(isoString).getTime();
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return 'just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} min ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hr ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
  };

  const syncCompanyCosts = async () => {
    const cid = authCompanyId;
    if (!cid || costSyncing) return;
    setCostSyncing(true);
    try {
      const response = await apiFetch(
        `/api/monitoring/usage/sync-costs?company_id=${encodeURIComponent(cid)}`,
        { method: 'POST' }
      );
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(
          getUserFacingErrorMessage(
            response.status,
            errData,
            `HTTP ${response.status}`
          )
        );
      }
      await fetchCompanyTokenHierarchy();
    } catch (err) {
      setCompanyHierarchyError(err.message || 'Failed to sync costs');
    } finally {
      setCostSyncing(false);
    }
  };

  const toggleCompanyTrackerJob = (jobId) => {
    setCompanyTrackerExpandedJobs((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const fetchModelPerformance = async () => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:fetchModelPerformance:ENTRY',message:'fetchModelPerformance called',data:{model_name:modelFilters.model_name,company_id:modelFilters.company_id},timestamp:Date.now(),sessionId:'debug-session',runId:'frontend-fetch',hypothesisId:'H6,H7'})}).catch(()=>{});
    // #endregion
    try {
      const params = new URLSearchParams();
      params.append('model_name', modelFilters.model_name);
      if (modelFilters.company_id) params.append('company_id', modelFilters.company_id);
      
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:fetchModelPerformance:BEFORE_FETCH',message:'Before fetch call',data:{url:`/api/monitoring/model-performance?${params}`,model_name:modelFilters.model_name,company_id:modelFilters.company_id},timestamp:Date.now(),sessionId:'debug-session',runId:'frontend-fetch',hypothesisId:'H6'})}).catch(()=>{});
      // #endregion
      
      const response = await apiFetch(`/api/monitoring/model-performance?${params}`);
      
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:fetchModelPerformance:AFTER_FETCH',message:'After fetch - response received',data:{status:response.status,ok:response.ok},timestamp:Date.now(),sessionId:'debug-session',runId:'frontend-fetch',hypothesisId:'H6'})}).catch(()=>{});
      // #endregion
      
      if (response.ok) {
        const data = await response.json();
        
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:fetchModelPerformance:RESPONSE_DATA',message:'Response data received',data:{success:data.success,has_data:data.data!==undefined,total_predictions:data.data?.total_predictions,avg_confidence:data.data?.avg_confidence},timestamp:Date.now(),sessionId:'debug-session',runId:'frontend-fetch',hypothesisId:'H7'})}).catch(()=>{});
        // #endregion
        
        if (data.success) {
          setModelPerformance(data.data);
          
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:fetchModelPerformance:STATE_SET',message:'State updated',data:{total_predictions:data.data?.total_predictions,avg_confidence:data.data?.avg_confidence},timestamp:Date.now(),sessionId:'debug-session',runId:'frontend-fetch',hypothesisId:'H7'})}).catch(()=>{});
          // #endregion
        } else {
          console.error('API error:', data.error);
          setModelPerformance(null);
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Failed to fetch model performance:', errorData);
        setModelPerformance(null);
      }
    } catch (err) {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:fetchModelPerformance:ERROR',message:'fetchModelPerformance ERROR',data:{error:err.message},timestamp:Date.now(),sessionId:'debug-session',runId:'frontend-fetch',hypothesisId:'H6'})}).catch(()=>{});
      // #endregion
      console.error('Error fetching model performance:', err);
      setModelPerformance(null);
    }
  };

  const fetchModelDrift = async () => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:fetchModelDrift:ENTRY',message:'fetchModelDrift called',data:{model_name:modelFilters.model_name,company_id:modelFilters.company_id,days:modelFilters.days},timestamp:Date.now(),sessionId:'debug-session',runId:'frontend-drift',hypothesisId:'DRIFT-1'})}).catch(()=>{});
    // #endregion
    try {
      const params = new URLSearchParams();
      params.append('model_name', modelFilters.model_name);
      if (modelFilters.company_id) params.append('company_id', modelFilters.company_id);
      params.append('days', modelFilters.days);
      
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:fetchModelDrift:BEFORE_FETCH',message:'Before drift fetch',data:{url:`/api/monitoring/model-performance/drift?${params}`,model_name:modelFilters.model_name,company_id:modelFilters.company_id,days:modelFilters.days},timestamp:Date.now(),sessionId:'debug-session',runId:'frontend-drift',hypothesisId:'DRIFT-1'})}).catch(()=>{});
      // #endregion
      
      const response = await apiFetch(`/api/monitoring/model-performance/drift?${params}`);
      
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:fetchModelDrift:AFTER_FETCH',message:'After drift fetch',data:{status:response.status,ok:response.ok},timestamp:Date.now(),sessionId:'debug-session',runId:'frontend-drift',hypothesisId:'DRIFT-1'})}).catch(()=>{});
      // #endregion
      
      if (response.ok) {
        const data = await response.json();
        
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:fetchModelDrift:RESPONSE',message:'Drift response data',data:{success:data.success,drift_detected:data.data?.drift_detected,message:data.data?.message,has_result:data.data!==undefined},timestamp:Date.now(),sessionId:'debug-session',runId:'frontend-drift',hypothesisId:'DRIFT-2'})}).catch(()=>{});
        // #endregion
        
        if (data.success) {
          setModelDrift(data.data);
        } else {
          console.error('API error:', data.error);
          setModelDrift(null);
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Failed to fetch model drift:', errorData);
        setModelDrift(null);
      }
    } catch (err) {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:fetchModelDrift:ERROR',message:'fetchModelDrift ERROR',data:{error:err.message},timestamp:Date.now(),sessionId:'debug-session',runId:'frontend-drift',hypothesisId:'DRIFT-1'})}).catch(()=>{});
      // #endregion
      console.error('Error fetching model drift:', err);
      setModelDrift(null);
    }
  };

  const fetchModelTrends = async () => {
    try {
      const params = new URLSearchParams();
      params.append('model_name', modelFilters.model_name);
      if (modelFilters.company_id) params.append('company_id', modelFilters.company_id);
      params.append('days', modelFilters.days);
      
      const response = await apiFetch(`/api/monitoring/model-performance/trends?${params}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setModelTrends(data.data?.trends || []);
        } else {
          console.error('API error:', data.error);
          setModelTrends([]);
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Failed to fetch model trends:', errorData);
        setModelTrends([]);
      }
    } catch (err) {
      console.error('Error fetching model trends:', err);
      setModelTrends([]);
    }
  };

  const fetchRAGMetrics = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (ragFilters.company_id) params.append('company_id', ragFilters.company_id);
      if (ragFilters.job_id) params.append('job_id', ragFilters.job_id);
      
      const response = await apiFetch(`/api/monitoring/rag-metrics/summary?${params}`);
      if (response.ok) {
        const data = await response.json();
        setRagMetrics(data.data);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching RAG metrics:', err);
      setError('Failed to load RAG metrics');
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount || 0);
  };

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms.toFixed(0)}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
    return `${(ms / 60000).toFixed(2)}min`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const renderOverview = () => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:renderOverview:entry',message:'renderOverview called',data:{hasOverviewData:!!overviewData,overviewDataKeys:overviewData?Object.keys(overviewData):[]},timestamp:Date.now(),sessionId:'debug-session',runId:'overview-debug',hypothesisId:'H3'})}).catch(()=>{});
    // #endregion
    if (!overviewData) return <div className="empty-state">No overview data available</div>;
    
    const { health, alerts, rag, cost, quality, confidence } = overviewData;
    
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:renderOverview:confidence_check',message:'Checking confidence data in render',data:{hasOverviewData:!!overviewData,overviewDataKeys:overviewData?Object.keys(overviewData):[],hasConfidence:!!confidence,confidenceType:typeof confidence,confidenceIsNull:confidence===null,confidenceIsUndefined:confidence===undefined,confidenceKeys:confidence?Object.keys(confidence):[],decisionConfidence:confidence?.decision_confidence,evidenceIntegrity:confidence?.evidence_integrity,summary:confidence?.summary},timestamp:Date.now(),sessionId:'debug-session',runId:'confidence-debug',hypothesisId:'H5'})}).catch(()=>{});
    // #endregion
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MonitoringDashboard.js:renderOverview:after_destructure',message:'After destructuring overviewData',data:{hasHealth:!!health,hasAlerts:!!alerts,hasRag:!!rag,hasCost:!!cost,healthStatus:health?.status,alertsSummary:alerts?.summary,costTotal:cost?.total_cost_usd,ragRetrievalStats:rag?.retrieval_stats},timestamp:Date.now(),sessionId:'debug-session',runId:'overview-debug',hypothesisId:'H4'})}).catch(()=>{});
    // #endregion
    
    return (
      <div className="overview-section">
        <div className="overview-grid">
          {/* Platform Status Badge (Demoted - Tiny) */}
          {health?.status && (
            <div className="platform-status-badge">
              <span className="status-indicator-small"></span>
              <span className="status-text-small">Platform operational ✓</span>
            </div>
          )}

          {/* Decision Risk Alert (Only if there are actual risks) */}
          {alerts?.summary && (alerts.summary.critical > 0 || alerts.summary.warning > 0) && (
            <div className="decision-risk-alert">
              <span className="alert-icon">⚠️</span>
              <span className="alert-text">
                {alerts.summary.critical > 0 
                  ? `${alerts.summary.critical} decision risk${alerts.summary.critical > 1 ? 's' : ''} detected in last 24h`
                  : `${alerts.summary.warning} warning${alerts.summary.warning > 1 ? 's' : ''} in last 24h`}
              </span>
            </div>
          )}
          {alerts?.summary && alerts.summary.critical === 0 && alerts.summary.warning === 0 && (
            <div className="decision-risk-alert positive">
              <span className="alert-icon">✓</span>
              <span className="alert-text">No decision risks detected in last 24h</span>
            </div>
          )}

          {/* Decision Confidence Summary Card (Customer-Facing) - Primary Focus */}
          <div className="metric-card decision-confidence-primary">
            <h3>Decision Confidence Summary</h3>
            {confidence ? (
              <div className="confidence-summary">
                {/* Row 1: Decision Trust Metrics (4 columns) */}
                <div className="trust-metrics-row">
                  {/* Decision Safety Rate (Aggregate) */}
                  {confidence.decision_safety && (
                    <div className="safety-rate-display">
                      <div className="safety-rate-label">
                        Decision Safety
                        <span 
                          className="info-icon" 
                          onClick={(e) => {
                            e.stopPropagation();
                            setInfoModal('decision-safety');
                          }}
                          title="Click for more information"
                        >
                          ⓘ
                        </span>
                      </div>
                      <div className="safety-rate-value">
                        {confidence.decision_safety.rate || 0}% Safe
                      </div>
                      <div className="safety-flags">
                        {confidence.decision_safety.flags?.no_hallucinations && (
                          <span className="safety-flag">✔ No hallucinations</span>
                        )}
                        {confidence.decision_safety.flags?.all_traceable && (
                          <span className="safety-flag">✔ All traceable</span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Decision Confidence (Per Resume) */}
                  <div className="confidence-display">
                    <div className="confidence-label">
                      Decision Confidence
                      <span 
                        className="info-icon" 
                        onClick={(e) => {
                          e.stopPropagation();
                          setInfoModal('decision-confidence');
                        }}
                        title="Click for more information"
                      >
                        ⓘ
                      </span>
                    </div>
                    <span className={`confidence-level ${confidence.decision_confidence?.level || 'unknown'}`}>
                      {confidence.decision_confidence?.level ? 
                        (confidence.decision_confidence.level === 'moderate' ? 'Moderate' :
                         confidence.decision_confidence.level === 'high' ? 'High' :
                         confidence.decision_confidence.level === 'low' ? 'Low' :
                         confidence.decision_confidence.level.charAt(0).toUpperCase() + confidence.decision_confidence.level.slice(1).replace('_', ' ')) : 
                        'Unknown'}
                    </span>
                    <span className="confidence-score">
                      {confidence.decision_confidence?.score || 0}%
                    </span>
                  </div>
                  
                  {/* Actionability Rate (NEW) */}
                  {confidence.actionability && (
                    <div className="actionability-display">
                      <div className="actionability-label">
                        Actionable Decisions
                        <span 
                          className="info-icon" 
                          onClick={(e) => {
                            e.stopPropagation();
                            setInfoModal('actionable-decisions');
                          }}
                          title="Click for more information"
                        >
                          ⓘ
                        </span>
                      </div>
                      <div className="actionability-value">
                        {confidence.actionability.rate || 0}% Ready
                      </div>
                      <div className="actionability-breakdown">
                        {confidence.actionability.minor_review_count > 0 && (
                          <span className="actionability-note">
                            {confidence.actionability.minor_review_count}% required minor review
                          </span>
                        )}
                        {confidence.actionability.blocked_count > 0 && (
                          <span className="actionability-note blocked">
                            {confidence.actionability.blocked_count}% blocked
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Unit Cost (NEW - Renamed from Cost Impact) */}
                  {confidence.cost_impact && (
                    <div className="cost-impact-display">
                      <div className="cost-impact-label">
                        Unit Cost
                        <span 
                          className="info-icon" 
                          onClick={(e) => {
                            e.stopPropagation();
                            setInfoModal('cost-impact');
                          }}
                          title="Click for more information"
                        >
                          ⓘ
                        </span>
                      </div>
                      <div className="cost-impact-value">
                        {(() => {
                          const cost = confidence.cost_impact.cost_per_resume || 0;
                          // Round to 2 decimal places, but show as ~$0.05 if small
                          if (cost < 1) {
                            return `~$${cost.toFixed(2)}`;
                          } else {
                            return `~$${cost.toFixed(2)}`;
                          }
                        })()}/resume
                      </div>
                      {confidence.cost_impact.total_resumes > 0 && (
                        <div className="cost-impact-note">
                          ({confidence.cost_impact.total_resumes} processed)
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Row 2: What to Pay Attention To */}
                <div className="attention-row">
                  {/* Risk Signals */}
                  <div className="risk-signals">
                    <div className="risk-signals-header">
                      Risk Signals
                      <span 
                        className="info-icon" 
                        onClick={(e) => {
                          e.stopPropagation();
                          setInfoModal('risk-signals');
                        }}
                        title="Click for more information"
                      >
                        ⓘ
                      </span>
                    </div>
                    <div className="risk-flags-list">
                      {confidence.evidence_integrity?.message && confidence.evidence_integrity.message.includes('Error') ? (
                        <div className="risk-flag-item error">
                          <span className="risk-flag-icon">⚠️</span>
                          <span className="risk-flag-text">{confidence.evidence_integrity.message}</span>
                        </div>
                      ) : confidence.evidence_integrity?.flags && confidence.evidence_integrity.flags.length > 0 ? (
                        confidence.evidence_integrity.flags.map((flag, idx) => (
                          <div key={idx} className="risk-flag-item">
                            <span className="risk-flag-icon">✔</span>
                            <span className="risk-flag-text">{flag}</span>
                          </div>
                        ))
                      ) : (
                        <div className="risk-flag-item">
                          <span className="risk-flag-icon">⚠️</span>
                          <span className="risk-flag-text">{confidence.evidence_integrity?.message || 'Risk assessment unavailable'}</span>
                        </div>
                      )}
                      {confidence.known_gaps && confidence.known_gaps.gaps && confidence.known_gaps.gaps.length > 0 && !confidence.evidence_integrity?.message?.includes('Error') && (
                        confidence.known_gaps.gaps.slice(0, 2).map((gap, idx) => (
                          <div key={`gap-${idx}`} className="risk-flag-item warning">
                            <span className="risk-flag-icon">▲</span>
                            <span className="risk-flag-text">{gap}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  
                  {/* Recommended Improvements (Renamed from Improvement Opportunities) */}
                  <div className="improvement-opportunities">
                    <div className="improvement-header">
                      Recommended Improvements
                      <span 
                        className="info-icon" 
                        onClick={(e) => {
                          e.stopPropagation();
                          setInfoModal('improvement-opportunities');
                        }}
                        title="Click for more information"
                      >
                        ⓘ
                      </span>
                    </div>
                    {confidence.improvement_opportunities && confidence.improvement_opportunities.length > 0 ? (
                      <ul className="improvement-list">
                        {confidence.improvement_opportunities.map((opp, idx) => (
                          <li key={idx} className="improvement-item">
                            <span className="improvement-icon">•</span>
                            <span className="improvement-text">{opp}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="improvement-empty">No improvement opportunities identified</div>
                    )}
                  </div>
                </div>
                
                {/* Row 3: Trust & Governance */}
                <div className="governance-row">
                  {/* Recommended Action (NEW) */}
                  {confidence.improvement_opportunities && confidence.improvement_opportunities.length > 0 && (
                    <div className="recommended-action">
                      <div className="recommended-action-header">
                        <span className="action-icon">→</span>
                        <span className="action-title">Recommended Action</span>
                      </div>
                      <div className="action-content">
                        {(() => {
                          // Get the first improvement opportunity as the recommended action
                          const firstOpp = confidence.improvement_opportunities[0];
                          // Create a more actionable message
                          if (firstOpp.includes('date') || firstOpp.includes('timeline')) {
                            return `Improve resume date extraction to increase tenure confidence by ~12%`;
                          } else if (firstOpp.includes('format')) {
                            return `Improve resume format parsing to increase extraction accuracy by ~8%`;
                          } else if (firstOpp.includes('JD')) {
                            return `Optimize JD structure handling to improve requirement coverage by ~10%`;
                          } else {
                            return `Address ${firstOpp.toLowerCase()} to improve decision quality`;
                          }
                        })()}
                      </div>
                    </div>
                  )}
                  
                  {/* Audit & Compliance Readiness (Renamed) */}
                  <div className="audit-ready">
                    <div className="audit-header">
                      <span className="audit-title">
                        Audit & Compliance Readiness
                        <span 
                          className="info-icon" 
                          onClick={(e) => {
                            e.stopPropagation();
                            setInfoModal('audit-ready');
                          }}
                          title="Click for more information"
                        >
                          ⓘ
                        </span>
                      </span>
                      <span className={`audit-badge ${confidence.audit_readiness?.ready ? 'ready' : 'not-ready'}`}>
                        {confidence.audit_readiness?.percentage || 0}%
                      </span>
                    </div>
                    {confidence.audit_readiness?.indicators && confidence.audit_readiness.indicators.length > 0 && (
                      <ul className="audit-indicators">
                        {confidence.audit_readiness.indicators.map((indicator, idx) => (
                          <li key={idx} className="audit-indicator-item">
                            <span className="audit-icon">✓</span>
                            <span className="audit-text">{indicator}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="audit-features">
                      <div className="audit-feature">• Every decision traceable</div>
                      <div className="audit-feature">• Source-linked explanations</div>
                      <div className="audit-feature">• Downloadable audit report</div>
                    </div>
                    
                    {/* Decision Scope & Human Override (NEW) - Show based on persona */}
                    {(personaView === 'unified' || personaView === 'recruiter' || personaView === 'compliance') && (
                      <div className="decision-scope">
                        <div className="scope-note">
                          <strong>Decision Scope:</strong> Confidence reflects <strong>role fit & skills match</strong>, not compensation or culture fit.
                        </div>
                        <div className="override-note">
                          <strong>Human Control:</strong> All decisions are reviewable and overrideable by humans.
                        </div>
                      </div>
                    )}
                    
                    {/* Executive Summary - Simplified view */}
                    {personaView === 'exec' && (
                      <div className="executive-summary">
                        <div className="exec-metric">
                          <span className="exec-label">Overall Trust Score:</span>
                          <span className="exec-value">
                            {Math.round(
                              (confidence.decision_confidence?.score || 0) * 0.4 +
                              (confidence.decision_safety?.rate || 0) * 0.3 +
                              (confidence.actionability?.rate || 0) * 0.3
                            )}%
                          </span>
                        </div>
                        <div className="exec-note">
                          {confidence.decision_safety?.rate >= 95 && confidence.actionability?.rate >= 90 ? (
                            <span className="exec-positive">✓ System operating at optimal trust levels</span>
                          ) : (
                            <span className="exec-warning">⚠ Some metrics need attention</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Trend Context (NEW) - Actual historical comparisons */}
                {confidence.trends && (
                  <div className="trend-context">
                    {confidence.trends.confidence_trend !== 'no_data' && confidence.trends.confidence_trend !== 'error' && (
                      <div className="trend-item">
                        <span className="trend-label">Decision Confidence:</span>
                        <span className={`trend-value ${
                          confidence.trends.confidence_trend === 'up' ? 'trend-up' :
                          confidence.trends.confidence_trend === 'down' ? 'trend-down' : ''
                        }`}>
                          {confidence.trends.confidence_trend === 'up' && '↑'}
                          {confidence.trends.confidence_trend === 'down' && '↓'}
                          {confidence.trends.confidence_trend === 'stable' && '→'}
                          {' '}
                          {confidence.trends.confidence_change > 0 ? '+' : ''}{confidence.trends.confidence_change}% 
                          {confidence.trends.confidence_previous > 0 && ` from ${confidence.trends.confidence_previous}%`}
                        </span>
                      </div>
                    )}
                    {confidence.trends.safety_trend !== 'no_data' && confidence.trends.safety_trend !== 'error' && (
                      <div className="trend-item">
                        <span className="trend-label">Decision Safety:</span>
                        <span className={`trend-value ${
                          confidence.trends.safety_trend === 'up' ? 'trend-up' :
                          confidence.trends.safety_trend === 'down' ? 'trend-down' : ''
                        }`}>
                          {confidence.trends.safety_trend === 'up' && '↑'}
                          {confidence.trends.safety_trend === 'down' && '↓'}
                          {confidence.trends.safety_trend === 'stable' && '→'}
                          {' '}
                          {confidence.trends.safety_change > 0 ? '+' : ''}{confidence.trends.safety_change}% 
                          {confidence.trends.safety_previous > 0 && ` from ${confidence.trends.safety_previous}%`}
                        </span>
                      </div>
                    )}
                    {confidence.trends.actionability_trend !== 'no_data' && confidence.trends.actionability_trend !== 'error' && (
                      <div className="trend-item">
                        <span className="trend-label">Actionability:</span>
                        <span className={`trend-value ${
                          confidence.trends.actionability_trend === 'up' ? 'trend-up' :
                          confidence.trends.actionability_trend === 'down' ? 'trend-down' : ''
                        }`}>
                          {confidence.trends.actionability_trend === 'up' && '↑'}
                          {confidence.trends.actionability_trend === 'down' && '↓'}
                          {confidence.trends.actionability_trend === 'stable' && '→'}
                          {' '}
                          {confidence.trends.actionability_change > 0 ? '+' : ''}{confidence.trends.actionability_change}% 
                          {confidence.trends.actionability_previous > 0 && ` from ${confidence.trends.actionability_previous}%`}
                        </span>
                      </div>
                    )}
                    {(!confidence.trends || confidence.trends.confidence_trend === 'no_data') && (
                      <div className="trend-item">
                        <span className="trend-label">Trends:</span>
                        <span className="trend-value">Stable over last {confidence.summary?.total_judgments || 0} resumes</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Advanced / Technical Details (Collapsible) */}
                <details className="technical-details">
                  <summary className="technical-details-summary">Advanced / Technical Details</summary>
                  <div className="internal-metrics">
                    {confidence.technical_metrics && (
                      <>
                        <div className="internal-metric-item">
                          <span className="internal-label">Data Quality:</span>
                          <span className="internal-value">
                            {confidence.technical_metrics.data_quality?.level ? 
                              confidence.technical_metrics.data_quality.level.charAt(0).toUpperCase() + confidence.technical_metrics.data_quality.level.slice(1) : 
                              'N/A'} ({confidence.technical_metrics.data_quality?.score || 'N/A'}/10)
                          </span>
                        </div>
                        <div className="internal-metric-item">
                          <span className="internal-label">Average Accuracy:</span>
                          <span className="internal-value">{confidence.technical_metrics.avg_accuracy?.toFixed(1) || 'N/A'}/10</span>
                        </div>
                        <div className="internal-metric-item">
                          <span className="internal-label">Average Relevance:</span>
                          <span className="internal-value">{confidence.technical_metrics.avg_relevance?.toFixed(1) || 'N/A'}/10</span>
                        </div>
                        <div className="internal-metric-item">
                          <span className="internal-label">Average Completeness:</span>
                          <span className="internal-value">{confidence.technical_metrics.avg_completeness?.toFixed(1) || 'N/A'}/10</span>
                        </div>
                        <div className="internal-metric-item">
                          <span className="internal-label">Total Judgments:</span>
                          <span className="internal-value">{confidence.technical_metrics.total_judgments || 0}</span>
                        </div>
                      </>
                    )}
                    {quality && (
                      <>
                        <div className="internal-metric-item">
                          <span className="internal-label">Format Compliance:</span>
                          <span className="internal-value">{quality.dimensions?.format_compliance?.toFixed(1) || 'N/A'}/10</span>
                        </div>
                        <div className="internal-metric-item">
                          <span className="internal-label">QA Strictness:</span>
                          <span className="internal-value">High (hallucination-safe)</span>
                        </div>
                        <div className="internal-metric-item">
                          <span className="internal-label">Pass Rate:</span>
                          <span className="internal-value">{quality.pass_rate?.toFixed(1) || 0}%</span>
                          <span className="internal-note">(Strict internal QA threshold)</span>
                        </div>
                      </>
                    )}
                  </div>
                </details>
              </div>
            ) : (
              <div className="empty-state">No confidence data available</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTracing = () => {
    return (
      <div className="tracing-section">
        <div className="section-filters">
          <input
            type="text"
            placeholder="Company ID"
            value={traceFilters.company_id}
            onChange={(e) => setTraceFilters({...traceFilters, company_id: e.target.value})}
          />
          <input
            type="text"
            placeholder="Operation/Agent"
            value={traceFilters.operation}
            onChange={(e) => setTraceFilters({...traceFilters, operation: e.target.value})}
          />
          <input
            type="date"
            placeholder="Start Date"
            value={traceFilters.start_date}
            onChange={(e) => setTraceFilters({...traceFilters, start_date: e.target.value})}
          />
          <input
            type="date"
            placeholder="End Date"
            value={traceFilters.end_date}
            onChange={(e) => setTraceFilters({...traceFilters, end_date: e.target.value})}
          />
          <button onClick={fetchTraces}>Refresh</button>
        </div>

        <div className="traces-layout">
          <div className="traces-list">
            <h3>Traces ({traces.length})</h3>
            {traces.length === 0 ? (
              <div className="empty-state">No traces found</div>
            ) : (
              <div className="trace-items">
                {traces.map((trace) => (
                  <div
                    key={trace.trace_id}
                    className={`trace-item ${selectedTrace === trace.trace_id ? 'selected' : ''}`}
                    onClick={() => setSelectedTrace(trace.trace_id)}
                  >
                    <div className="trace-id">{trace.trace_id.substring(0, 16)}...</div>
                    <div className="trace-info">
                      <span>{trace.span_count} spans</span>
                      <span>{formatDuration(trace.duration_ms)}</span>
                    </div>
                    <div className="trace-time">{formatDate(trace.start_time)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="trace-details">
            {selectedTrace && traceDetails ? (
              <>
                <h3>Trace Details</h3>
                <div className="trace-detail-info">
                  <div><strong>Trace ID:</strong> {traceDetails.trace_id}</div>
                  <div><strong>Duration:</strong> {formatDuration(traceDetails.duration_ms)}</div>
                  <div><strong>Spans:</strong> {traceDetails.span_count}</div>
                  <div><strong>Start:</strong> {formatDate(traceDetails.start_time)}</div>
                  <div><strong>End:</strong> {formatDate(traceDetails.end_time)}</div>
                </div>
                <div className="spans-list">
                  <h4>Spans</h4>
                  {traceDetails.spans?.map((span, idx) => (
                    <div key={idx} className="span-item">
                      <div className="span-header">
                        <span className="span-operation">{span.operation}</span>
                        <span className={`span-status ${span.status.toLowerCase()}`}>{span.status}</span>
                      </div>
                      <div className="span-details">
                        <div>Duration: {formatDuration(span.duration_ms)}</div>
                        <div>Time: {formatDate(span.start_time)}</div>
                        {span.attributes && (
                          <div className="span-attributes">
                            <div>Agent: {span.attributes.agent_name}</div>
                            <div>Model: {span.attributes.model}</div>
                            <div>Provider: {span.attributes.provider}</div>
                            <div>Tokens In: {span.attributes.tokens_in}</div>
                            <div>Tokens Out: {span.attributes.tokens_out}</div>
                            <div>Cost: {formatCurrency(span.attributes.cost_usd)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="empty-state">Select a trace to view details</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCompanyTokenTracker = () => {
    const summary = companyHierarchy?.company_summary;
    const jobs = companyHierarchy?.jobs || [];
    const costMeta = companyHierarchy?.cost_meta || null;
    const totalJobs = companyTrackerMeta?.total_jobs ?? jobs.length;
    const totalPages = companyTrackerMeta?.total_pages ?? 0;
    const currentPage = companyTrackerMeta?.page ?? companyTrackerPage;

    const rangeLabel = companyTrackerRange === 'all'
      ? 'All time'
      : `Last ${companyTrackerRange} days`;

    return (
      <div className="cost-section company-token-tracker-section">
        <div className="company-tracker-header">
          <h2 className="company-tracker-title">Company token usage</h2>
          <div className="company-tracker-actions">
            <span className="company-tracker-label-inline">Time range:</span>
            <select
              value={companyTrackerRange}
              onChange={(e) => { setCompanyTrackerRange(e.target.value); setCompanyTrackerPage(1); }}
              className="company-tracker-days-select"
              disabled={companyTrackerViewMode === 'personal'}
              title={companyTrackerViewMode === 'personal' ? 'Range is auto-locked in personal mode' : 'Select company-wide reporting window'}
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="all">All time</option>
            </select>
            <span className="company-tracker-label-inline">View:</span>
            <select
              value={companyTrackerViewMode}
              onChange={(e) => { setCompanyTrackerViewMode(e.target.value); setCompanyTrackerPage(1); }}
              className="company-tracker-days-select"
              aria-label="Company token tracker view mode"
            >
              <option value="official">Official (full period)</option>
              <option value="personal">Since my reset</option>
            </select>
            <button
              type="button"
              className="btn-secondary"
              onClick={resetCompanyTokenHierarchy}
              disabled={companyTrackerResetting || companyTrackerViewMode !== 'personal'}
              title={
                companyTrackerViewMode === 'personal'
                  ? 'Set your personal baseline from now'
                  : 'Switch to "Since my reset" to enable Reset'
              }
            >
              {companyTrackerResetting ? 'Resetting…' : 'Reset data'}
            </button>
          </div>
        </div>

        {companyHierarchyError && (
          <p className="token-usage-error-inline">{companyHierarchyError}</p>
        )}

        {!companyHierarchyError && companyHierarchy && (
          <>
            <p className="company-tracker-context-line">
              {companyHierarchy.company_name || `Company ID: ${companyHierarchy.company_id}`} • {companyTrackerViewMode === 'personal' ? 'Range: From reset to now.' : rangeLabel}
            </p>
            <p className="company-tracker-context-line company-tracker-context-emphasis">
              {companyTrackerViewMode === 'official'
                ? 'Showing official company totals for the selected period.'
                : (
                  companyHierarchy?.checkpoint_since
                    ? `Showing company totals from your reset baseline (${new Date(companyHierarchy.checkpoint_since).toLocaleString()}).`
                    : 'No reset baseline found yet. Click "Reset data" to start personal tracking from now.'
                )}
            </p>
            <div className="metric-card company-tracker-summary-card">
              <h3>{companyHierarchy.company_name || 'YOUR COMPANY'}</h3>
              <p className="company-tracker-subtitle">Company ID: {companyHierarchy.company_id}</p>
              <div className="company-tracker-summary-table-wrap">
                <table className="token-usage-table company-tracker-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Input tokens</th>
                      <th>Output tokens</th>
                      <th>Total</th>
                      <th>Cost (USD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="company-total-row">
                      <td>Company total</td>
                      <td>{(summary?.tokens_in_total ?? 0).toLocaleString()}</td>
                      <td>{(summary?.tokens_out_total ?? 0).toLocaleString()}</td>
                      <td>{(summary?.total_tokens ?? 0).toLocaleString()}</td>
                      <td>{formatCurrency(summary?.cost_total_usd || 0)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="metric-card company-tracker-hierarchy-card">
              <h3>Usage by job and resume</h3>
              <div className="section-filters" style={{ marginBottom: 12 }}>
                <input
                  type="text"
                  placeholder="Search jobs/resumes"
                  value={companyTrackerSearch}
                  onChange={(e) => { setCompanyTrackerSearch(e.target.value); setCompanyTrackerPage(1); }}
                />
                <select
                  value={companyTrackerPageSize}
                  onChange={(e) => { setCompanyTrackerPageSize(Number(e.target.value)); setCompanyTrackerPage(1); }}
                >
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </select>
              </div>
              <div className="token-usage-table-container">
                <table className="token-usage-table company-tracker-table company-tracker-hierarchy-table">
                  <thead>
                    <tr>
                      <th>Job / Resume</th>
                      <th>Input tokens</th>
                      <th>Output tokens</th>
                      <th>Total</th>
                      <th>Cost (USD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => {
                      const expanded = companyTrackerExpandedJobs.has(job.job_id);
                      const hasResumes = job.resumes && job.resumes.length > 0;
                      const jobLabel = `Job: ${job.job_title} (JD #${job.job_id})`;
                      return (
                        <React.Fragment key={job.job_id}>
                          <tr
                            className={`company-tracker-job-row ${hasResumes ? 'clickable' : ''}`}
                            onClick={() => hasResumes && toggleCompanyTrackerJob(job.job_id)}
                            tabIndex={hasResumes ? 0 : undefined}
                          >
                            <td title={jobLabel}>
                              {hasResumes && (expanded ? '▼ ' : '▶ ')}
                              {jobLabel}
                            </td>
                            <td>{job.tokens_in.toLocaleString()}</td>
                            <td>{job.tokens_out.toLocaleString()}</td>
                            <td>{job.total_tokens.toLocaleString()}</td>
                            <td>{formatCurrency(job.cost_total_usd || 0)}</td>
                          </tr>
                          {expanded && hasResumes && job.resumes.map((r, idx) => {
                            const rawResumeName = r.resume_name || 'Unknown';
                            const isResumeRow = r.resume_id !== null && r.resume_id !== undefined;
                            const resumeLabel = isResumeRow
                              ? ('Resume: ' + rawResumeName + (r.resume_id ? ' (#' + r.resume_id + ')' : ''))
                              : rawResumeName;
                            return (
                              <tr key={r.resume_id || idx} className="company-tracker-resume-row">
                                <td title={resumeLabel}>
                                  {idx === job.resumes.length - 1 ? '└─ ' : '├─ '}
                                  {resumeLabel}
                                </td>
                                <td>{r.tokens_in.toLocaleString()}</td>
                                <td>{r.tokens_out.toLocaleString()}</td>
                                <td>{r.total_tokens.toLocaleString()}</td>
                                <td>{formatCurrency(r.cost_total_usd || 0)}</td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {jobs.length === 0 && (
                <p className="token-usage-empty">No job-level usage in this window. Usage is stored per company, job, and resume.</p>
              )}
              <div className="company-tracker-footer-note" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Data from LLM usage logs. Only your company&apos;s usage is shown.</span>
                <span>
                  {totalJobs} job{totalJobs === 1 ? '' : 's'} • Page {currentPage}{totalPages ? ` of ${totalPages}` : ''}
                </span>
              </div>
              <div className="company-tracker-footer-note" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span>
                  Cost computed locally
                  {costMeta?.pricing_refreshed_at
                    ? ` · Prices updated ${formatSyncAge(costMeta.pricing_refreshed_at)}`
                    : ''}
                  {costMeta?.last_synced_at
                    ? ` · Last synced ${formatSyncAge(costMeta.last_synced_at)}`
                    : ''}
                </span>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={syncCompanyCosts}
                  disabled={costSyncing}
                  style={{ padding: '2px 10px', fontSize: '0.8rem' }}
                  title="Fetch costs from Langfuse for rows with unknown model pricing"
                >
                  {costSyncing ? 'Syncing…' : '↻ Sync missing costs'}
                </button>
              </div>
              {totalPages > 1 && (
                <div className="jobs-pagination">
                  <button
                    type="button"
                    className="jobs-page-btn"
                    onClick={() => setCompanyTrackerPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    className="jobs-page-btn"
                    onClick={() => setCompanyTrackerPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>

          </>
        )}

        {!companyHierarchyError && !companyHierarchy && (
          <p className="token-usage-empty">Loading company token usage…</p>
        )}
      </div>
    );
  };


  const renderModelPerformance = () => {
    return (
      <div className="model-performance-section">
        <div className="section-filters">
          <select
            value={modelFilters.model_name}
            onChange={(e) => setModelFilters({...modelFilters, model_name: e.target.value})}
          >
            <option value="evaluator">Evaluator</option>
            <option value="extractor">Extractor</option>
            <option value="summarizer">Summarizer</option>
          </select>
          <input
            type="text"
            placeholder="Company ID"
            value={modelFilters.company_id}
            onChange={(e) => setModelFilters({...modelFilters, company_id: e.target.value})}
          />
          <input
            type="number"
            placeholder="Days"
            value={modelFilters.days}
            onChange={(e) => setModelFilters({...modelFilters, days: e.target.value})}
          />
          <button
            onClick={() => {
              if (SHOW_RELIABILITY_METRICS) fetchModelPerformance();
              fetchModelDrift();
              if (SHOW_RELIABILITY_TRENDS) fetchModelTrends();
            }}
          >
            Refresh
          </button>
        </div>

        <div className="model-performance-grid">
          {SHOW_RELIABILITY_METRICS && (
          <div className="performance-metrics-card">
            <h3>
              Reliability Metrics
              <span 
                className="info-icon" 
                onClick={(e) => {
                  e.stopPropagation();
                  setInfoModal('reliability-metrics');
                }}
                title="Click for more information"
              >
                ⓘ
              </span>
            </h3>
            {modelPerformance ? (
              <div className="metrics-list">
                {/* ✅ CHANGED: Accuracy → Avg Confidence (Reliability) */}
                <div className="metric-row">
                  <span>
                    Avg Confidence (Reliability):
                    <span 
                      className="info-icon" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setInfoModal('avg-confidence');
                      }}
                      title="Click for more information"
                    >
                      ⓘ
                    </span>
                  </span>
                  <span>
                    {modelPerformance.avg_confidence != null ? (modelPerformance.avg_confidence * 100).toFixed(1) + '%' : 'N/A'}
                  </span>
                </div>
                
                {/* ✅ CHANGED: Total Evaluated → Samples Observed */}
                <div className="metric-row">
                  <span>Samples Observed:</span>
                  <span>{modelPerformance.total_predictions || 0}</span>
                </div>
                
                {/* ✅ CHANGED: Correct Predictions → With Ground Truth (if available) */}
                {modelPerformance.predictions_with_ground_truth > 0 ? (
                  <div className="metric-row">
                    <span>
                      With Ground Truth:
                      <span 
                        className="info-icon" 
                        onClick={(e) => {
                          e.stopPropagation();
                          setInfoModal('ground-truth');
                        }}
                        title="Click for more information"
                      >
                        ⓘ
                      </span>
                    </span>
                    <span>{modelPerformance.predictions_with_ground_truth}</span>
                  </div>
                ) : (
                  <div className="metric-row subtle">
                    <span className="subtle-text">
                      ⓘ Accuracy metrics unavailable (no ground truth data)
                    </span>
                  </div>
                )}
                
                {/* Optional: Show accuracy only if ground truth exists */}
                {modelPerformance.accuracy !== null && modelPerformance.accuracy !== undefined && 
                 modelPerformance.predictions_with_ground_truth > 0 ? (
                  <div className="metric-row">
                    <span>
                      Accuracy (with ground truth):
                      <span 
                        className="info-icon" 
                        onClick={(e) => {
                          e.stopPropagation();
                          setInfoModal('accuracy-metric');
                        }}
                        title="Click for more information"
                      >
                        ⓘ
                      </span>
                    </span>
                    <span>{(modelPerformance.accuracy * 100).toFixed(1)}%</span>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="empty-state">No reliability data available</div>
            )}
          </div>
          )}

          {/* ✅ ENHANCED: Drift Detection with context */}
          <div className="drift-detection-card">
            <h3>
              Drift Detection
              <span 
                className="info-icon" 
                onClick={(e) => {
                  e.stopPropagation();
                  setInfoModal('drift-detection');
                }}
                title="Click for more information"
              >
                ⓘ
              </span>
            </h3>
            {modelDrift ? (
              <div className="drift-info">
                <div className={'drift-status ' + (modelDrift.drift_detected ? 'drift-detected' : 'no-drift')}>
                  {modelDrift.drift_detected ? '⚠️ Drift Detected' : '✓ No Drift'}
                </div>
                
                {/* ✅ ADDED: Context explanation */}
                {modelDrift.detection_method && (
                  <div className="drift-context">
                    <small>
                      {modelDrift.detection_method === 'confidence_distribution' 
                        ? 'Evaluating confidence distribution changes (historical comparison)'
                        : modelDrift.detection_method === 'confidence_distribution_split'
                        ? 'Evaluating confidence distribution changes (split-period comparison)'
                        : modelDrift.detection_method === 'prediction_distribution'
                        ? 'Evaluating prediction pattern changes'
                        : 'Evaluating output reliability patterns'}
                    </small>
                    {modelDrift.comparison_type === 'split_period' && (
                      <small style={{display: 'block', marginTop: '4px', fontStyle: 'italic'}}>
                        ℹ️ Comparing first half vs second half of current period (insufficient historical data)
                      </small>
                    )}
                    {modelDrift.message && (
                      <small style={{display: 'block', marginTop: '4px'}}>
                        {modelDrift.message}
                      </small>
                    )}
                  </div>
                )}
                
                {modelDrift.p_value !== null && modelDrift.p_value !== undefined && (
                  <div className="drift-details">
                    <div>P-value: {modelDrift.p_value?.toFixed(4)}</div>
                    {modelDrift.ks_statistic !== null && modelDrift.ks_statistic !== undefined ? (
                      <div>KS Statistic: {modelDrift.ks_statistic?.toFixed(4)}</div>
                    ) : null}
                    {modelDrift.chi2_statistic !== null && modelDrift.chi2_statistic !== undefined ? (
                      <div>Chi² Statistic: {modelDrift.chi2_statistic?.toFixed(4)}</div>
                    ) : null}
                    <div>Threshold: {modelDrift.threshold}</div>
                    
                    {/* ✅ ADDED: Confidence shift indicator */}
                    {modelDrift.confidence_shift !== null && modelDrift.confidence_shift !== undefined ? (
                      <div className={'confidence-shift ' + (modelDrift.confidence_shift >= 0 ? 'positive' : 'negative')}>
                        Confidence Shift: {modelDrift.confidence_shift >= 0 ? '+' : ''}
                        {(modelDrift.confidence_shift * 100).toFixed(2)}%
                      </div>
                    ) : null}
                    
                    {/* ✅ ADDED: Coverage metrics */}
                    {modelDrift.confidence_coverage && (
                      <div className="coverage-info">
                        <small>
                          Confidence Coverage: {((modelDrift.confidence_coverage.current || 0) * 100).toFixed(0)}% 
                          (current), {((modelDrift.confidence_coverage.previous || 0) * 100).toFixed(0)}% (previous)
                        </small>
                      </div>
                    )}
                  </div>
                )}
                {modelDrift.message && (
                  <div className="drift-message">{modelDrift.message}</div>
                )}
              </div>
            ) : (
              <div className="empty-state">Insufficient data for drift detection</div>
            )}
          </div>

          {SHOW_RELIABILITY_TRENDS && (
          <div className="performance-trends-card">
            <h3>
              Reliability Trends ({modelFilters.days} days)
              <span 
                className="info-icon" 
                onClick={(e) => {
                  e.stopPropagation();
                  setInfoModal('reliability-trends');
                }}
                title="Click for more information"
              >
                ⓘ
              </span>
            </h3>
            {modelTrends.length > 0 ? (
              <div className="trend-chart">
                {/* ✅ ADDED: Reference bands */}
                <div className="trend-reference-bands">
                  <div className="band healthy" style={{ bottom: '60%' }}>
                    <span>Healthy (0.6-0.8)</span>
                  </div>
                  <div className="band warning" style={{ bottom: '40%' }}>
                    <span>Review (0.4-0.6)</span>
                  </div>
                </div>
                
                {modelTrends.map((trend, idx) => {
                  const maxConf = Math.max(...modelTrends.map(t => t.avg_confidence || 0), 1);
                  const height = ((trend.avg_confidence || 0) / maxConf) * 100;
                  const confidenceValue = (trend.avg_confidence || 0) * 100;
                  const bandClass = confidenceValue >= 60 ? 'healthy' : confidenceValue >= 40 ? 'warning' : 'low';
                  
                  return (
                    <div key={idx} className="trend-bar-container">
                      <div className={'trend-bar ' + bandClass} style={{ height: height + '%' }}>
                        <span className="trend-value">{confidenceValue.toFixed(1)}%</span>
                      </div>
                      <div className="trend-label">{trend.date}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">No trend data available</div>
            )}
          </div>
          )}
        </div>
      </div>
    );
  };

  const renderRAGMetrics = () => {
    if (!ragMetrics) return <div className="empty-state">No RAG metrics available</div>;
    
    const { retrieval_stats, chunk_quality_stats, rag_chain_stats } = ragMetrics;
    
    return (
      <div className="rag-metrics-section">
        <div className="section-filters">
          <input
            type="text"
            placeholder="Company ID"
            value={ragFilters.company_id}
            onChange={(e) => setRagFilters({...ragFilters, company_id: e.target.value})}
          />
          <input
            type="text"
            placeholder="Job ID"
            value={ragFilters.job_id}
            onChange={(e) => setRagFilters({...ragFilters, job_id: e.target.value})}
          />
          <button onClick={fetchRAGMetrics}>Refresh</button>
        </div>

        <div className="rag-metrics-grid">
          <div className="rag-metric-card">
            <h3>Retrieval Stats</h3>
            {retrieval_stats ? (
              <div className="metric-details">
                <div>Total Retrievals: {retrieval_stats.total_retrievals || 0}</div>
                <div>Total Chunks: {retrieval_stats.total_chunks_retrieved || 0}</div>
                <div>Avg Chunks/Retrieval: {retrieval_stats.avg_chunks_per_retrieval?.toFixed(2) || 'N/A'}</div>
                <div>Avg Similarity: {(retrieval_stats.avg_similarity_score * 100)?.toFixed(1) || 'N/A'}%</div>
                <div>Avg Retrieval Time: {formatDuration(retrieval_stats.avg_retrieval_time_ms)}</div>
                <div>Hybrid Search: {retrieval_stats.hybrid_search_percentage?.toFixed(1) || 'N/A'}%</div>
              </div>
            ) : (
              <div className="empty-state">No retrieval data</div>
            )}
          </div>

          <div className="rag-metric-card">
            <h3>Chunk Quality</h3>
            {chunk_quality_stats ? (
              <div className="metric-details">
                <div>Total Chunks: {chunk_quality_stats.total_chunks_evaluated || 0}</div>
                <div>Avg Relevance: {(chunk_quality_stats.avg_relevance_score * 100)?.toFixed(1) || 'N/A'}%</div>
                <div>Core Requirements: {chunk_quality_stats.core_requirement_chunks_percentage?.toFixed(1) || 'N/A'}%</div>
                {chunk_quality_stats.section_distribution && (
                  <div className="section-distribution">
                    <strong>Sections:</strong>
                    {Object.entries(chunk_quality_stats.section_distribution).map(([section, count]) => (
                      <div key={section}>{section}: {count}</div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="empty-state">No chunk quality data</div>
            )}
          </div>

          <div className="rag-metric-card">
            <h3>RAG Chain Stats</h3>
            {rag_chain_stats ? (
              <div className="metric-details">
                <div>Total Invocations: {rag_chain_stats.total_invocations || 0}</div>
                <div>Avg Total Time: {formatDuration(rag_chain_stats.avg_total_time_ms)}</div>
                <div>Avg Retrieval Time: {formatDuration(rag_chain_stats.avg_retrieval_time_ms)}</div>
                <div>Avg LLM Time: {formatDuration(rag_chain_stats.avg_llm_time_ms)}</div>
                <div>Avg Context Length: {rag_chain_stats.avg_context_length?.toFixed(0) || 'N/A'} tokens</div>
                <div>Avg Answer Length: {rag_chain_stats.avg_answer_length?.toFixed(0) || 'N/A'} chars</div>
              </div>
            ) : (
              <div className="empty-state">No RAG chain data</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading && activeTab === 'overview') {
    return (
      <div className="monitoring-dashboard">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading monitoring data...</p>
        </div>
      </div>
    );
  }

  return (
      <div className="monitoring-dashboard">
        <div className="dashboard-header">
          <h1>AI Decision Trust Dashboard</h1>
          <div className="persona-selector">
            <label htmlFor="persona-select">View:</label>
            <select 
              id="persona-select"
              value={personaView} 
              onChange={(e) => setPersonaView(e.target.value)}
              className="persona-select"
            >
              <option value="unified">Unified View</option>
              <option value="exec">Executive Summary</option>
              <option value="recruiter">Recruiter View</option>
              <option value="compliance">Compliance View</option>
            </select>
          </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="dashboard-tabs">
        {showOverviewTab && (
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        )}
        <button
          className={activeTab === 'model-performance' ? 'active' : ''}
          onClick={() => setActiveTab('model-performance')}
        >
          Model Performance
        </button>
        <button
          className={activeTab === 'company-tracker' ? 'active' : ''}
          onClick={() => setActiveTab('company-tracker')}
        >
          Companies Token Tracker
        </button>
      </div>

      <div className="dashboard-content">
        {showOverviewTab && activeTab === 'overview' && renderOverview()}
        {activeTab === 'model-performance' && renderModelPerformance()}
        {activeTab === 'company-tracker' && renderCompanyTokenTracker()}
      </div>

      {/* Info Modal */}
      {infoModal && (
        <div className="info-modal-overlay" onClick={() => setInfoModal(null)}>
          <div className="info-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="info-modal-header">
              <h3>
                 {infoModal === 'decision-safety' && 'Decision Safety'}
                 {infoModal === 'decision-confidence' && 'Decision Confidence'}
                 {infoModal === 'actionable-decisions' && 'Actionable Decisions'}
                 {infoModal === 'cost-impact' && 'Unit Cost'}
                 {infoModal === 'risk-signals' && 'Risk Signals'}
                 {infoModal === 'improvement-opportunities' && 'Recommended Improvements'}
                 {infoModal === 'audit-ready' && 'Audit & Compliance Readiness'}
                 {infoModal === 'reliability-metrics' && 'Reliability Metrics'}
                 {infoModal === 'avg-confidence' && 'Avg Confidence (Reliability)'}
                 {infoModal === 'ground-truth' && 'Ground Truth Data'}
                 {infoModal === 'accuracy-metric' && 'Accuracy Metric'}
                 {infoModal === 'drift-detection' && 'Drift Detection'}
                 {infoModal === 'reliability-trends' && 'Reliability Trends'}
              </h3>
              <button className="info-modal-close" onClick={() => setInfoModal(null)}>×</button>
            </div>
            <div className="info-modal-body">
              {infoModal === 'decision-safety' && (
                <>
                  <h4>What it means:</h4>
                  <p>Decision Safety measures the percentage of decisions that are safe to use - meaning they have no hallucinations (fabricated information), all decisions are traceable back to their source, and evidence integrity has been verified.</p>
                  <h4>How it's calculated:</h4>
                  <ul>
                    <li>Checks each quality judgment for <strong>zero hallucinations</strong> (no fabricated/invented information)</li>
                    <li>Verifies that <strong>all decisions are traceable</strong> (has reasoning/explanation linking back to resume/JD)</li>
                    <li>Confirms <strong>evidence integrity</strong> (passed quality checks)</li>
                    <li>Safety Rate = (Safe Decisions / Total Decisions) × 100%</li>
                  </ul>
                  <p><strong>What it means for you:</strong> 100% Safe means all decisions are reliable with no fabricated data. Lower percentages indicate some decisions may need review.</p>
                </>
              )}
              {infoModal === 'decision-confidence' && (
                <>
                  <h4>What it means:</h4>
                  <p>Decision Confidence measures how confident the system is in its evaluation decisions for each resume. It combines accuracy, relevance, and completeness into a single confidence score.</p>
                  <h4>How it's calculated:</h4>
                  <ul>
                    <li><strong>Accuracy</strong> (40% weight): How correct the extracted information is</li>
                    <li><strong>Relevance</strong> (40% weight): How relevant the information is to the job</li>
                    <li><strong>Completeness</strong> (20% weight): How complete the information is</li>
                    <li>Confidence Score = (Accuracy × 0.4) + (Relevance × 0.4) + (Completeness × 0.2)</li>
                    <li>Converted to percentage: (Score / 10.0) × 100%</li>
                  </ul>
                  <h4>Confidence Levels:</h4>
                  <ul>
                    <li><strong>High (80-100%)</strong>: Strong trust signal - very reliable decisions</li>
                    <li><strong>Moderate (60-79%)</strong>: Acceptable for decision support - may need some review</li>
                    <li><strong>Low (&lt;60%)</strong>: Needs attention - should be reviewed before use</li>
                  </ul>
                </>
              )}
              {infoModal === 'actionable-decisions' && (
                <>
                  <h4>What it means:</h4>
                  <p>Actionable Decisions measures the percentage of decisions that are immediately usable for hiring without being blocked by system errors or critical issues.</p>
                  <h4>How it's calculated:</h4>
                  <ul>
                    <li><strong>Actionable</strong>: Decisions that passed quality checks and can be used immediately</li>
                    <li><strong>Blocked</strong>: Decisions blocked due to:
                      <ul>
                        <li>System errors</li>
                        <li>Critical issues (hallucinations, fabricated data)</li>
                        <li>Invalid data</li>
                      </ul>
                    </li>
                    <li><strong>Minor Review</strong>: Low quality but not blocked - may need quick review</li>
                    <li>Actionability Rate = (Actionable Decisions / Total Decisions) × 100%</li>
                  </ul>
                  <p><strong>What it means for you:</strong> 100% Ready means all decisions can be used immediately. Lower percentages indicate more decisions are blocked and need fixing.</p>
                </>
              )}
               {infoModal === 'cost-impact' && (
                 <>
                   <h4>What it means:</h4>
                   <p>Unit Cost shows the average cost per resume processed, including all LLM API calls (extraction, evaluation, summarization, etc.).</p>
                   <h4>How it's calculated:</h4>
                   <ul>
                     <li>Total LLM API costs from all agents (extractor, evaluator, summarizer, formatter)</li>
                     <li>Divided by the number of unique resumes processed</li>
                     <li>Unit Cost = Total Cost / Number of Resumes</li>
                     <li>Includes all token usage and API call costs</li>
                   </ul>
                   <p><strong>What it means for you:</strong> This shows the cost efficiency of your resume analysis. Higher costs per resume may indicate opportunities for optimization (e.g., using cheaper models, reducing token usage, or caching results).</p>
                 </>
               )}
               {infoModal === 'risk-signals' && (
                 <>
                   <h4>What it means:</h4>
                   <p>Risk Signals identify potential issues that could affect decision reliability. These are binary flags (present/absent) rather than numeric scores, making them easier to understand and act upon.</p>
                   <h4>What we check:</h4>
                   <ul>
                     <li><strong>No unsupported claims</strong>: Verifies that all information is backed by evidence from the resume or job description</li>
                     <li><strong>No fabricated experience</strong>: Confirms no invented skills, roles, or qualifications</li>
                     <li><strong>Missing data gaps</strong>: Highlights incomplete information that may affect decision quality (e.g., missing experience dates, unclear contact info)</li>
                   </ul>
                   <p><strong>What it means for you:</strong> Green checkmarks (✔) indicate safe decisions. Warning icons (⚠️) highlight areas that may need attention but don't necessarily block the decision.</p>
                 </>
               )}
               {infoModal === 'improvement-opportunities' && (
                 <>
                   <h4>What it means:</h4>
                   <p>Recommended Improvements identify actionable areas where system performance can be enhanced. These are practical, prioritized suggestions rather than diagnostic metrics.</p>
                   <h4>Common improvements:</h4>
                   <ul>
                     <li><strong>Resume format parsing</strong>: Improve extraction accuracy for non-standard resume formats</li>
                     <li><strong>JD structure optimization</strong>: Better handling of job descriptions with unclear structure</li>
                     <li><strong>Experience timeline extraction</strong>: More accurate date and duration parsing</li>
                     <li><strong>Contact information extraction</strong>: Better capture of email, phone, and location data</li>
                   </ul>
                   <p><strong>What it means for you:</strong> These are prioritized suggestions based on actual system performance. Addressing these can improve overall decision quality and reduce manual review needs.</p>
                 </>
               )}
               {infoModal === 'audit-ready' && (
                 <>
                   <h4>What it means:</h4>
                   <p>Audit & Compliance Readiness measures how well-prepared your system is for compliance audits, legal reviews, or regulatory inspections. It focuses on traceability and evidence integrity.</p>
                   <h4>What we check:</h4>
                   <ul>
                     <li><strong>Decision traceability</strong>: Every decision has reasoning/explanation linking back to source documents</li>
                     <li><strong>Source-linked explanations</strong>: Explanations reference specific resume sections or JD requirements</li>
                     <li><strong>Evidence integrity</strong>: All claims are verifiable from source documents</li>
                   </ul>
                   <h4>Readiness Levels:</h4>
                   <ul>
                     <li><strong>100% Ready (Green)</strong>: All decisions are fully traceable and source-linked - ready for audit</li>
                     <li><strong>&lt;95% Ready (Yellow)</strong>: Some decisions lack full traceability - may need improvement before audit</li>
                   </ul>
                   <p><strong>What it means for you:</strong> High audit readiness means you can confidently explain any decision to compliance officers, lawyers, or regulators. This is critical for enterprise deployments and regulated industries.</p>
                 </>
               )}
               {infoModal === 'reliability-metrics' && (
                 <>
                   <h4>What it means:</h4>
                   <p>Reliability Metrics reflect output quality signals (completeness, consistency, confidence) rather than ground-truth accuracy. These metrics indicate how reliable the system believes its outputs are based on internal quality assessments.</p>
                   <h4>Key Differences from Accuracy:</h4>
                   <ul>
                     <li><strong>Reliability</strong>: Measures the system's self-assessment of output quality (completeness, consistency, confidence signals)</li>
                     <li><strong>Accuracy</strong>: Requires human-verified ground truth labels to measure correctness</li>
                     <li><strong>Why this matters</strong>: In production, we rarely have ground truth, so reliability metrics provide actionable quality signals</li>
                   </ul>
                   <h4>What we track:</h4>
                   <ul>
                     <li><strong>Avg Confidence</strong>: Average confidence score indicating output reliability</li>
                     <li><strong>Samples Observed</strong>: Total number of predictions/evaluations tracked</li>
                     <li><strong>With Ground Truth</strong>: Number of samples with human-verified labels (only when available)</li>
                   </ul>
                   <p><strong>What it means for you:</strong> Higher reliability scores suggest more complete and consistent outputs. These are quality signals, not correctness guarantees - they help you understand when the system is confident in its outputs.</p>
                 </>
               )}
               {infoModal === 'avg-confidence' && (
                 <>
                   <h4>What it means:</h4>
                   <p>Average Confidence (Reliability) is the mean confidence score (0-1, displayed as 0-100%) indicating how reliable the system believes its outputs are. Higher values suggest more complete and consistent outputs.</p>
                   <h4>Important Distinction:</h4>
                   <p><strong>This is NOT accuracy</strong> - it reflects the system's self-assessment of output quality, not ground-truth correctness.</p>
                   <h4>How it's calculated:</h4>
                   <ul>
                     <li><strong>Evaluator Agent</strong>: Confidence = overall_score / 100.0 (normalized from 0-100 to 0-1)</li>
                     <li><strong>Extractor Agent</strong>: Confidence based on extraction completeness (name, skills, experience, education, contact info)</li>
                     <li><strong>Summarizer Agent</strong>: Confidence based on evaluation score (50%), summary completeness (30%), and length quality (20%)</li>
                     <li>Average = Sum of all confidence scores / Number of predictions</li>
                   </ul>
                   <h4>Confidence Ranges:</h4>
                   <ul>
                     <li><strong>0.6-0.8 (60-80%)</strong>: Healthy range - indicates reliable outputs</li>
                     <li><strong>0.4-0.6 (40-60%)</strong>: Review recommended - outputs may need attention</li>
                     <li><strong>&lt;0.4 (&lt;40%)</strong>: Low confidence - outputs likely incomplete or inconsistent</li>
                   </ul>
                   <p><strong>What it means for you:</strong> Use this as a quality signal. Higher confidence suggests the system believes its outputs are more reliable. However, always verify critical decisions regardless of confidence level.</p>
                 </>
               )}
               {infoModal === 'ground-truth' && (
                 <>
                   <h4>What it means:</h4>
                   <p>Ground Truth Data refers to human-verified labels that indicate the "correct" answer for a prediction. This is used to calculate accuracy metrics, but is rarely available in production systems.</p>
                   <h4>Why it's important:</h4>
                   <ul>
                     <li><strong>Accuracy calculation</strong>: Requires comparing predictions against ground truth labels</li>
                     <li><strong>Production reality</strong>: Most systems don't have ground truth - decisions are made without verified "correct" answers</li>
                     <li><strong>Reliability vs Accuracy</strong>: Without ground truth, we use reliability signals (confidence, completeness) instead</li>
                   </ul>
                   <h4>When ground truth is available:</h4>
                   <ul>
                     <li>Accuracy metrics become available and are displayed</li>
                     <li>You can see precision, recall, and F1 scores</li>
                     <li>System can learn from verified correct/incorrect predictions</li>
                   </ul>
                   <p><strong>What it means for you:</strong> If you see "Accuracy metrics unavailable (no ground truth data)", this is normal for production systems. The system still provides reliability metrics (confidence scores) that indicate output quality.</p>
                 </>
               )}
               {infoModal === 'accuracy-metric' && (
                 <>
                   <h4>What it means:</h4>
                   <p>Accuracy Metric shows the percentage of predictions that match human-verified ground truth labels. This is only available when ground truth data has been provided.</p>
                   <h4>How it's calculated:</h4>
                   <ul>
                     <li>Compare each prediction against its ground truth label</li>
                     <li>Count correct matches (prediction == ground truth)</li>
                     <li>Accuracy = (Correct Predictions / Total Predictions with Ground Truth) × 100%</li>
                   </ul>
                   <h4>Limitations:</h4>
                   <ul>
                     <li><strong>Requires ground truth</strong>: Only available when human-verified labels are provided</li>
                     <li><strong>Production rarity</strong>: Most production systems don't have ground truth for every prediction</li>
                     <li><strong>Not real-time</strong>: Ground truth is typically added after decisions are made (for training/evaluation)</li>
                   </ul>
                   <p><strong>What it means for you:</strong> When available, accuracy provides a correctness measure. When unavailable (most common), rely on reliability metrics (confidence scores) to assess output quality.</p>
                 </>
               )}
               {infoModal === 'drift-detection' && (
                 <>
                   <h4>What it means:</h4>
                   <p>Drift Detection monitors changes in confidence/reliability signal distributions over time. It does NOT measure changes in accuracy or correctness - it tracks whether output reliability patterns have shifted.</p>
                   <h4>How it works:</h4>
                   <ul>
                     <li><strong>Primary Method</strong>: Uses Kolmogorov-Smirnov (KS) test to compare confidence score distributions between two time periods</li>
                     <li><strong>Fallback Method</strong>: If confidence unavailable, uses Chi-square test on prediction patterns</li>
                     <li><strong>Time periods</strong>: 
                       <ul>
                         <li><strong>Historical comparison</strong> (preferred): Compares current period (last 30 days) vs previous period (30-60 days ago)</li>
                         <li><strong>Split-period comparison</strong> (when no history): If insufficient historical data, splits current period into first half vs second half for comparison</li>
                       </ul>
                     </li>
                     <li><strong>Minimum data</strong>: Requires at least 10 predictions in each period (or 20 total for split comparison)</li>
                   </ul>
                   <h4>What drift indicates:</h4>
                   <ul>
                     <li><strong>Distribution shift</strong>: The pattern of confidence scores has changed</li>
                     <li><strong>Reliability change</strong>: Output reliability patterns have shifted (not necessarily worse, just different)</li>
                     <li><strong>Statistical significance</strong>: P-value &lt; threshold (default 0.1) indicates significant change</li>
                   </ul>
                   <h4>Important Notes:</h4>
                   <ul>
                     <li><strong>Not accuracy drift</strong>: This measures reliability signal changes, not correctness changes</li>
                     <li><strong>Requires history</strong>: Needs data from two time periods (current + previous 30 days)</li>
                     <li><strong>Early systems</strong>: May show "Insufficient data" until enough historical data accumulates</li>
                   </ul>
                   <p><strong>What it means for you:</strong> Drift detection helps identify when output reliability patterns change. This can indicate system changes, data distribution shifts, or model behavior changes. Review drift alerts to understand what changed and why.</p>
                 </>
               )}
               {infoModal === 'reliability-trends' && (
                 <>
                   <h4>What it means:</h4>
                   <p>Reliability Trends show average confidence/reliability scores over time. Higher values indicate more reliable outputs. This reflects the system's self-assessment of output quality, not ground-truth accuracy.</p>
                   <h4>What the chart shows:</h4>
                   <ul>
                     <li><strong>Daily averages</strong>: Average confidence score for each day in the selected period</li>
                     <li><strong>Color coding</strong>: 
                       <ul>
                         <li><strong>Green (0.6-0.8)</strong>: Healthy range - reliable outputs</li>
                         <li><strong>Yellow (0.4-0.6)</strong>: Review recommended - may need attention</li>
                         <li><strong>Red (&lt;0.4)</strong>: Low confidence - outputs likely incomplete</li>
                       </ul>
                     </li>
                     <li><strong>Trend direction</strong>: Shows whether reliability is improving, declining, or stable</li>
                   </ul>
                   <h4>How to interpret:</h4>
                   <ul>
                     <li><strong>Upward trend</strong>: Reliability improving over time (good sign)</li>
                     <li><strong>Downward trend</strong>: Reliability declining (may indicate issues)</li>
                     <li><strong>Stable trend</strong>: Consistent reliability (expected for mature systems)</li>
                     <li><strong>Volatile trend</strong>: High variance may indicate inconsistent inputs or system instability</li>
                   </ul>
                   <h4>Important Notes:</h4>
                   <ul>
                     <li><strong>Not accuracy trends</strong>: These are reliability signals, not correctness measurements</li>
                     <li><strong>Agent-specific</strong>: Trends are shown per agent (Evaluator, Extractor, Summarizer)</li>
                     <li><strong>Time period</strong>: Adjustable via the "Days" filter (default: 30 days)</li>
                   </ul>
                   <p><strong>What it means for you:</strong> Use trends to monitor system health over time. Consistent high reliability suggests stable, quality outputs. Declining trends may indicate issues that need investigation.</p>
                 </>
               )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitoringDashboard;

